/**
 * API client for the AI-powered slice suggestion endpoint.
 * Keeps all AI network logic outside of UI components.
 *
 * Uses `credentials: 'include'` so the httpOnly session cookie is sent
 * automatically — no token parameter required.
 */
import { AiSliceSet, InsightNode, InsightsSummary, SuggestedOpportunity } from '../types';
import { Quality } from '../components/ui/QualitySlider';
import { getJson, postJson, deleteJson, RequestOptions } from './httpClient';
import { isErrorResponse } from '../utils/apiUtils';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Network-failure messages. Endpoints that reach the Python AI backend name it
 * specifically; the plain persistence endpoints in this module (usage, summary
 * deletion) say "the server" instead. Both distinctions predate httpClient and
 * are surfaced to users, so they are kept.
 */
const AI_SERVICE_UNREACHABLE: RequestOptions = { networkErrorMessage: 'Network error: could not reach AI service.' };
const SERVER_UNREACHABLE: RequestOptions = { networkErrorMessage: 'Network error: could not reach the server.' };

/**
 * Maps a user's "AI model quality" selection (Settings) to a concrete provider/model
 * identifier the server forwards to the Python AI backend as `preferredModel`. Shared
 * across every AI feature that honors this setting — everything except the codebase
 * scan, which always forces 'high' server-side regardless of the client's selection
 * (see server/server.js POST /api/ai/repo-scan).
 */
export function qualityToModel(q?: Quality): string | undefined {
    // Model IDs must match valid Groq API identifiers. Groq deprecated `llama-3.1-8b-instant`
    // and `llama-3.3-70b-versatile` for free/dev-tier accounts on 2026-08-16 (requests to
    // those IDs now 404) — migrated to Groq's recommended replacements. Vendor-prefixed IDs
    // like `openai/gpt-oss-*` are still Groq-hosted models, not calls to the real OpenAI API.
    if (q === 'low') return 'openai/gpt-oss-20b';
    if (q === 'medium') return 'openai/gpt-oss-120b';
    if (q === 'high') return 'claude-sonnet-4-6';
    return undefined;
}

export interface SuggestSlicesParams {
    /** Whether we are slicing an opportunity node or a story/solution node. */
    nodeType: 'opportunity' | 'story';
    /**
     * The type of child nodes to create from this slice.
     * Determines which structured fields the AI produces:
     * 'story' → asA, iWant, soThat, acceptanceCriteria
     * 'experiment' → assumption, method, successMetric
     * Defaults to 'story' on the server when omitted.
     */
    childType?: 'story' | 'experiment';
    /** Preferred model quality selected in settings. Optional — server may ignore. */
    modelQuality?: Quality;
    parentTitle: string;
    parentDescription?: string;
    /** The root outcome title, used by the AI to maintain goal alignment. */
    outcomeTitle?: string;
    /** Ancestor nodes from root down to (but not including) the node being sliced. */
    ancestorNodes?: { type: string; title: string; description?: string }[];
    /** Insight nodes linked to any node in the direct slicing hierarchy (ancestors + the node being sliced). */
    hierarchyInsights?: { title: string; description: string }[];
    /**
     * Nodes soft-connected to the node being sliced via the drag-connect mechanism.
     * Provides adjacent relational context (work happening in other branches of the tree).
     * direction: 'incoming' means the peer node connects TO the slicing target;
     *            'outgoing' means the slicing target connects TO the peer node.
     */
    connectedNodes?: { direction: 'incoming' | 'outgoing'; type: string; title: string; description?: string }[];
    /** Number of distinct slice sets to return (1–10). Defaults to 3 on the server. */
    count?: number;
}

export interface AiSuggestSlicesResponse {
    sliceSets: AiSliceSet[];
    /** The model name used to generate the suggestions (e.g. 'claude-sonnet-4-6', 'meta-llama/llama-4-scout-17b-16e-instruct'). */
    aiModel?: string;
}

/**
 * Error shape shared by all 5 AI endpoints. `creditsUsed`/`creditLimit`/`period` are
 * only present when `error === 'ai_limit_reached'` (see server/aiUsageLimiter.js) —
 * consumed by src/pricing/aiUsageLimit.ts to trigger the upgrade prompt.
 */
export interface AiActionError {
    error: string;
    message?: string;
    creditsUsed?: number;
    creditLimit?: number;
    period?: string;
}

/**
 * Requests AI-generated slice suggestions from the backend.
 * Returns 3 distinct slice sets, each with a label and array of titled slices.
 */
export async function suggestSlices(
    params: SuggestSlicesParams,
): Promise<AiSuggestSlicesResponse | AiActionError> {
    const bodyData: Record<string, unknown> = { ...params };
    if (params.modelQuality) bodyData.preferredModel = qualityToModel(params.modelQuality);

    return postJson<AiSuggestSlicesResponse>('/api/ai/suggest-slices', bodyData, AI_SERVICE_UNREACHABLE);
}

/**
 * Requests AI-generated opportunity suggestions from project insights.
 */
export async function suggestOpportunities(
    projectId: string,
    insights: InsightNode[],
    options?: { force?: boolean; customInstructions?: string; modelQuality?: Quality },
): Promise<{ opportunities?: SuggestedOpportunity[]; noNewInsights?: boolean } & Partial<AiActionError>> {
    return postJson('/api/ai/opportunity-suggestions', {
        projectId,
        insights,
        force: options?.force ?? false,
        customInstructions: options?.customInstructions || undefined,
        preferredModel: qualityToModel(options?.modelQuality),
    }, AI_SERVICE_UNREACHABLE);
}

/**
 * Streams an AI-generated insights summary for a project.
 * Calls `onChunk` for each token received; resolves with the stored summary
 * on completion, or an error string on failure.
 */
export async function generateInsightsSummary(
    projectId: string,
    insights: InsightNode[],
    onChunk: (text: string) => void,
    customInstructions?: string,
    modelQuality?: Quality,
): Promise<{ summary?: InsightsSummary } & Partial<AiActionError>> {
    // Deliberately not via httpClient: this endpoint streams, so the caller needs
    // the Response itself to read resp.body — the client's parse-to-JSON contract
    // would consume it. The rest of this module goes through httpClient.
    try {
        const resp = await fetch(`${API_BASE}/api/ai/insights-summary`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                projectId,
                insights,
                customInstructions: customInstructions || undefined,
                preferredModel: qualityToModel(modelQuality),
            }),
        });

        if (!resp.ok) {
            const text = await resp.text();
            try { return JSON.parse(text); } catch { return { error: `Server error (${resp.status})` }; }
        }

        const reader = resp.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? '';

            for (const line of lines) {
                if (!line.startsWith('data: ')) continue;
                const jsonStr = line.slice(6).trim();
                if (!jsonStr) continue;
                try {
                    const parsed = JSON.parse(jsonStr);
                    if (parsed.text) onChunk(parsed.text);
                    if (parsed.done && parsed.summary) return { summary: parsed.summary as InsightsSummary };
                    if (parsed.error) return { error: parsed.error };
                } catch { /* malformed SSE line — skip */ }
            }
        }

        return {};
    } catch {
        return { error: 'Network error: could not reach AI service.' };
    }
}

export interface RepoArchitectureDoc {
    modules: unknown[];
    tech_stack: unknown[];
    complexity_signals: unknown[];
    notable_areas: unknown[];
    summary: string;
    [key: string]: unknown;
}

export interface ScanRepoArchitectureResult {
    architectureDoc?: RepoArchitectureDoc;
    scannedAt?: string;
    nextAvailableAt?: string;
    error?: string;
    creditsUsed?: number;
    creditLimit?: number;
}

/**
 * Triggers a "Scan Codebase" architecture scan for the given repo (pro/teams only,
 * rate-limited to once per 7 days per user — see server.js POST /api/ai/repo-scan).
 */
export async function scanRepoArchitecture(repoFullName: string): Promise<ScanRepoArchitectureResult> {
    return postJson('/api/ai/repo-scan', { repoFullName }, AI_SERVICE_UNREACHABLE);
}

export interface RepoScanStatus {
    hasScan?: boolean;
    scannedAt?: string | null;
    nextAvailableAt?: string | null;
    error?: string;
}

/**
 * Checks whether a successful repo scan exists for the given repo, and when the
 * next scan is allowed. Used to gate the per-story "Estimate with AI" button.
 */
export async function getRepoScanStatus(repoFullName: string): Promise<RepoScanStatus> {
    return getJson(`/api/ai/repo-scan/status?repoFullName=${encodeURIComponent(repoFullName)}`, AI_SERVICE_UNREACHABLE);
}

export interface RepoArchitectureResult {
    architectureDoc?: RepoArchitectureDoc;
    scannedAt?: string;
    error?: string;
}

/**
 * Fetches the most recent successful architecture document for the given repo, so
 * the user can re-read a past scan's summary at any time. Unlike scanRepoArchitecture
 * this is a plain read and is not rate-limited.
 */
export async function getRepoArchitectureDoc(repoFullName: string): Promise<RepoArchitectureResult> {
    return getJson(`/api/ai/repo-scan/latest?repoFullName=${encodeURIComponent(repoFullName)}`, AI_SERVICE_UNREACHABLE);
}

export interface EstimateStoryPointsFields {
    asA?: string;
    iWant?: string;
    soThat?: string;
    acceptanceCriteria?: string;
    assumption?: string;
    method?: string;
    successMetric?: string;
}

export interface EstimateStoryPointsResult {
    storySize?: string;
    rationale?: string;
    error?: string;
    message?: string;
    creditsUsed?: number;
    creditLimit?: number;
}

/** A sibling story/experiment already sized, used as a relative sizing anchor. */
export interface EstimateCalibrationExample {
    title: string;
    summary?: string;
    size: string;
}

/**
 * Requests an AI-suggested t-shirt-size estimate (XS/S/M/L/XL) for a Story/Experiment,
 * grounded in the most recent repo architecture scan (see
 * server.js POST /api/ai/estimate-story). Fails with `error: 'no_scan'` when no
 * successful scan exists yet for the connected repo.
 *
 * `calibration` (optional) lists already-sized sibling stories/experiments so the
 * new estimate is sized relative to them rather than reasoned about in isolation.
 * Pass an empty/omitted list for the first-ever estimate in a project.
 */
export async function estimateStoryPoints(
    repoFullName: string,
    fields: EstimateStoryPointsFields,
    calibration?: EstimateCalibrationExample[],
    modelQuality?: Quality,
): Promise<EstimateStoryPointsResult> {
    return postJson('/api/ai/estimate-story', {
        repoFullName,
        ...fields,
        calibration: calibration || [],
        preferredModel: qualityToModel(modelQuality),
    }, AI_SERVICE_UNREACHABLE);
}

export interface AiUsageResponse {
    creditsUsed: number;
    creditLimit: number;
    period: string;
    /** True for zeytune_team users, who bypass metering entirely. */
    unlimited: boolean;
    error?: string;
}

/**
 * Fetches the current month's AI credit consumption for the authenticated user
 * (see server.js GET /api/ai/usage and server/aiUsageLimiter.js).
 */
export async function fetchAiUsage(): Promise<AiUsageResponse> {
    const result = await getJson<AiUsageResponse>('/api/ai/usage', SERVER_UNREACHABLE);
    // Callers read creditsUsed/creditLimit unconditionally, so a failure is
    // widened back into a full zeroed response that carries the error.
    return isErrorResponse(result)
        ? { creditsUsed: 0, creditLimit: 0, period: '', unlimited: false, error: result.error }
        : result;
}

/**
 * Permanently deletes a single generated insights summary from a project,
 * identified by its `generatedAt` timestamp.
 */
export async function deleteInsightsSummary(
    projectId: string,
    generatedAt: string,
): Promise<{ success?: boolean; error?: string }> {
    return deleteJson(
        `/api/projects/${projectId}/insights-summary/${encodeURIComponent(generatedAt)}`,
        SERVER_UNREACHABLE,
    );
}
